import React, { useCallback, useState } from 'react';
import { FormHelperText, FormLabel, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { AddCircle as AddCircleIcon } from '@mui/icons-material';
import cn from 'classnames';

import useMemoObject from '@dyl/hooks/useMemoObject';
import EditableChip from '@dyl/components/EditableChip';

const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
    },
    label: {
        marginBottom: theme.spacing(1),
    },
    chipsContainer: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
    },
    chip: {
        maxWidth: 256,
        marginBottom: theme.spacing(1),

        '&:not(:last-child)': {
            marginRight: theme.spacing(1),
        },
    },
    chip_new: {
        borderStyle: 'dashed',
    },
}));

/**
 * @param {Object} props
 * @param {string[]} [props.value]
 * @param {React.Dispatch<React.SetStateAction<string[]>>} props.onChange
 * @param {boolean} [props.disabled]
 * @param {string} [props.label]
 * @param {string} [props.helperText]
 * @param {boolean} [props.error]
 * @param {boolean} [props.required]
 * @param {number} [props.max]
 * @param {boolean} [props.isPictureEditing]
 * @param {boolean} [props.isComicStoryA1]
 * @param {boolean} [props.isNoteTaking]
 * @param {boolean} [props.isOnlineMessages]
 * @param {boolean} [props.isSpellingOrCSPreA1]
 * @param {number} [props.index]
 * @param {Function} [props.onArrayChange]
 * @param {number} [props.charLimit]
 */
export default function ChipList({
    value: array,
    onChange,
    className: propsClass,
    disabled,
    label,
    helperText,
    error,
    required = false,
    max = Infinity,
    readOnly = false,
    targetDictionary = null,
    transformFunction = null,
    maxLen = Infinity,
    isPictureEditing = false,
    isComicStoryA1 = false,
    isNoteTaking = false,
    isOnlineMessages = false,
    isSpellingOrCSPreA1 = false,
    ...other
}) {
    // Shallow-memoize, to avoid excess updates. Default to empty.
    const charLimits = {
        pictureEditing: 8,
        comicStoryA1: 25,
        noteTaking: 15,
        onlineMessages: 100,
        spellingOrCSPreA1: 12,
    };

    const currentCharLimit =
        (isPictureEditing && charLimits.pictureEditing) ||
        (isComicStoryA1 && charLimits.comicStoryA1) ||
        (isNoteTaking && charLimits.noteTaking) ||
        (isOnlineMessages && charLimits.onlineMessages) ||
        (isSpellingOrCSPreA1 && charLimits.spellingOrCSPreA1) ||
        Number.MAX_SAFE_INTEGER;

    array = !!transformFunction ? array?.map(transformFunction) : array;
    array = useMemoObject(array ?? []);

    const classes = useStyles();

    const [newValue, setNewValue] = useState('');
    const onNewValueChange = (event) => {
        let val = event?.target?.value ?? null;
        if (val.length > currentCharLimit) {
            return;
        } else {
            val = transformFunction?.(val) ?? val;
            setNewValue(val);
        }
    };

    const strLen = array.reduce((x, y) => x + y.length, 0) + (newValue?.length ?? 0);
    const isAtMaxArr = array.length >= max;
    const isAtMaxStr = strLen >= maxLen;

    const onAdd = () => {
        let inputValue = newValue.trim();
        inputValue = inputValue.replace(/^,+|,+$/g, '');

        if (inputValue) {
            let commaSeparated = inputValue
                .split(',')
                .map(element => element.trim().slice(0, currentCharLimit));

            const currentLength = (array || []).reduce((acc, item) => acc + item.length, 0);
            const newLength = commaSeparated.reduce((acc, item) => acc + item.length, 0);

            if (currentLength + newLength > maxLen) {
                let allowedLength = maxLen - currentLength;
                commaSeparated = commaSeparated.filter(input => {
                    if (allowedLength >= input.length) {
                        allowedLength -= input.length;
                        return true;
                    }
                    return false;
                });
            } else {
                commaSeparated = commaSeparated.filter(input => input.length);
            }
            if (max ===1 && commaSeparated.length > 1) {
                return;
            }

            const updatedArray = [...(array || []), ...commaSeparated];
            onChange(updatedArray);
            setNewValue('');
        }
    };

    /**
     * Also allow adding by pressing enter while the add element is focused.
     * @type {React.KeyboardEventHandler<HTMLDivElement>}
     */
    const onAddKeyDown = event => {
        if (isAtMaxArr) return;
        if (event.key === 'Enter' && strLen < maxLen) {
            onAdd();
        }
    };

    const getColor = useCallback((value) => {
        if (!targetDictionary) return 'primary';
        let color = 'chip';
        const valid = targetDictionary[value];
        if (valid !== undefined)
            color = targetDictionary[value] ? 'primary' : 'error';
        return color;
    }, [targetDictionary]);

    return (
        <div className={cn(classes.root, propsClass)}>
            <FormLabel error={error} className={classes.label}>
                {label}{required ? ' *' : ''}
            </FormLabel>

            <div className={classes.chipsContainer}>
                {array.map((value, index) => (
                    <Item
                        key={index}
                        index={index}
                        value={value}
                        onArrayChange={onChange}
                        className={classes.chip}
                        color={getColor(value)}
                        disabled={readOnly}
                        charLimit={currentCharLimit}
                        {...other}
                    />
                ))}

                {!disabled && (
                    <Tooltip title={isAtMaxArr ? `Max ${max}` : isAtMaxStr ? `Max ${maxLen}` : ''}>
                        <EditableChip
                            className={cn(classes.chip, classes.chip_new)}
                            color='primary'
                            variant='outlined'
                            InputProps={{
                                disabled: isAtMaxArr || isAtMaxStr,
                                placeholder: 'New',
                                style: { minWidth: 32 },
                            }}
                            value={newValue}
                            onChange={onNewValueChange}
                            // NB: The 'delete' button is replaced with an 'add' button here
                            onDelete={disabled ? undefined : onAdd}
                            deleteIcon={<AddCircleIcon />}
                            onKeyDown={onAddKeyDown}
                            disabled={disabled || isAtMaxArr}
                        />
                    </Tooltip>
                )}
            </div>

            <FormHelperText error={error}>
                {helperText}
            </FormHelperText>
        </div>
    );
}

function Item({ index, value, onArrayChange, charLimit = Number.MAX_SAFE_INTEGER, ...other }) {
    const onChange = useCallback(
        /** @type {React.ChangeEventHandler<HTMLInputElement>} */
        (event) => {
            const inputValue = event.target.value;
            const isInserting = inputValue.length > value.length;
            const isAtLimit = value.length >= charLimit;

            if (isInserting && isAtLimit) {
                return;
            }

            let updatedValue = inputValue.trim();
            if (updatedValue === '') {
                return;
            }
            if (updatedValue.length > charLimit) {
                updatedValue = updatedValue.slice(0, charLimit);
            }

            onArrayChange((array) =>
                array.map((old, i) => (i === index ? updatedValue : old))
            );
        },
        [index, value, onArrayChange, charLimit]
    );

    const onDelete = useCallback(
        /** @param {Event} event */
        event => {
            onArrayChange(array => array.filter((t, i) => i !== index));

            event.stopPropagation();
            event.preventDefault();
        }, [index, onArrayChange]
    );

    return (
        <EditableChip
            key={index}
            value={value}
            onChange={onChange}
            onDelete={onDelete}
            color='primary'
            {...other}
        />
    );
}